import React, { useEffect, useState } from 'react';
import { getTodayIST } from '../../../utils/dateIST';

/**
 * SchedulePickerDialog — Phase 4.0b shared modal for the receptionist's
 * "Schedule only" and "Collect & schedule" actions.
 *
 * Modes:
 *   variant='schedule'        → just date + time (Schedule only path)
 *   variant='collectSchedule' → date + time + payment mode + amount
 *                                (Collect & schedule path; payment fields
 *                                 mirror the existing PromptDialog)
 *
 * Returns the chosen values via onConfirm; caller fires the API.
 *
 * Props:
 *   open          — bool
 *   variant       — 'schedule' | 'collectSchedule'
 *   procedureName — string for the header
 *   defaultAmount — number (only used when variant='collectSchedule')
 *   busy          — bool, disables Confirm
 *   error         — optional error string
 *   onConfirm({ slot_date, slot_time, payment_mode?, amount?, waiver_amount? })
 *   onClose()
 */
function todayIsoLocal() {
  return getTodayIST();
}

export default function SchedulePickerDialog({
  open,
  variant = 'schedule',
  procedureName = 'procedure',
  defaultAmount = 0,
  busy = false,
  error = '',
  onConfirm,
  onClose,
}) {
  const [date, setDate] = useState(todayIsoLocal());
  const [time, setTime] = useState('10:00');
  // No payment-mode default — receptionist must pick UPI / CASH / CARD
  // explicitly. Empty string blocks submission via canSubmit below.
  const [paymentMode, setPaymentMode] = useState('');
  const [amount, setAmount] = useState(String(defaultAmount || 0));
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (open) {
      setDate(todayIsoLocal());
      setTime('10:00');
      setPaymentMode('');
      setAmount(String(defaultAmount || 0));
      setLocalError('');
    }
  }, [open, defaultAmount]);

  if (!open) return null;

  const isCollect = variant === 'collectSchedule';
  const canSubmit = !!date && !!time
    && (!isCollect || (Number(amount) >= 0 && !!paymentMode));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (isCollect && !paymentMode) {
      setLocalError('Please select a payment method.');
      return;
    }
    if (!canSubmit) return;
    setLocalError('');
    onConfirm?.({
      slot_date: date,
      slot_time: time,
      payment_mode: isCollect ? paymentMode : undefined,
      amount: isCollect ? String(amount || 0) : undefined,
    });
  };

  const inputCls = 'bg-white text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600';
  const inputStyle = {
    padding: '0.4rem 0.625rem',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    fontSize: '0.875rem',
    width: '100%',
    outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isCollect ? 'Collect & schedule appointment' : 'Schedule appointment'}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100"
        style={{
          width: 'min(420px, 100%)',
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          padding: '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true">📅</span>
          <strong style={{ flex: 1, fontSize: '0.9375rem' }}>
            {isCollect ? 'Collect & schedule' : 'Schedule appointment'}
          </strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="px-2 py-0.5 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.75rem' }}
          >
            ✕
          </button>
        </header>

        <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.85 }}>
          For: <strong>{procedureName}</strong>
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="sched-date" style={{ fontSize: '0.6875rem', opacity: 0.85, display: 'block', marginBottom: 4 }}>
              Date
            </label>
            <input
              id="sched-date"
              type="date"
              value={date}
              min={todayIsoLocal()}
              onChange={(e) => setDate(e.target.value)}
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="sched-time" style={{ fontSize: '0.6875rem', opacity: 0.85, display: 'block', marginBottom: 4 }}>
              Time
            </label>
            <input
              id="sched-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className={inputCls}
              style={inputStyle}
            />
          </div>
        </div>

        {isCollect && (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="sched-amount" style={{ fontSize: '0.6875rem', opacity: 0.85, display: 'block', marginBottom: 4 }}>
                  Amount (₹)
                </label>
                <input
                  id="sched-amount"
                  type="number"
                  min="0"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={inputCls}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="sched-mode" style={{ fontSize: '0.6875rem', opacity: 0.85, display: 'block', marginBottom: 4 }}>
                  Payment mode
                </label>
                <select
                  id="sched-mode"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className={inputCls}
                  style={inputStyle}
                >
                  <option value="" disabled>Select payment method…</option>
                  <option value="UPI">UPI</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                </select>
              </div>
            </div>
          </>
        )}

        {(localError || error) && (
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#b91c1c' }}>
            {localError || error}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.8125rem' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm font-semibold
                       hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-slate-900
                       disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy
              ? (isCollect ? 'Collecting…' : 'Scheduling…')
              : (isCollect ? 'Collect & schedule' : 'Schedule')}
          </button>
        </div>
      </form>
    </div>
  );
}
